import { Search } from "@mui/icons-material"
import { Autocomplete, Box, Button, IconButton, Paper, TableCell, TableContainer, TextField, Typography } from "@mui/material"
import React, { useState } from "react"
import { useRecoilValue, useRecoilValueLoadable } from "recoil"
import { groupDescriptorDicState, sdAllGroupedState, ServiceDescriptorGrouped, usePreferitoForSdExists } from "../atoms/portale"
import { useIsLocalhost } from "../utils/hooks"
import TableView from "../framework/ui/view/table"
import { Star } from '../svgicons/Star';
import { StarFill } from '../svgicons/StarFill';
// import { InfoCircle } from '../svgicons/InfoCircle';
import { ServiceDescriptor } from "../models/ServiceDescriptor"
import { Stickies } from '../svgicons/Stickies';
import { useStartApplication } from "./StartApplication"
import { useEditPreferito } from "../atoms/preferito"
import { notPrefStarColor, prefStarColor } from "../Constants"

type SearchSectionPropsType = {
    placeholer?: string,
}
const searchWidth = '100%'

export const SearchSection: React.FC<SearchSectionPropsType> = ({ placeholer }) => {
    const startApplication = useStartApplication()
    const isLocalhost = useIsLocalhost()
    const allSd = useRecoilValueLoadable(sdAllGroupedState)
    const [value, setValue] = useState<ServiceDescriptorGrouped | null>(null)
    const plh = placeholer || 'Cerca applicazione...'
    const handleFilterOptions = (options: ServiceDescriptorGrouped[], state: { inputValue: string, getOptionLabel: (o: ServiceDescriptorGrouped) => string }) => {
        const { inputValue } = state
        if (inputValue === '' && options.length > 200) return []
        return options.filter(o => o.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0)
    }
    const handleExecute = (sd: ServiceDescriptor) => {
        sd && startApplication(sd)
        value && setValue(null)
    }
    return (
        <Box sx={{ alignSelf: 'center', width: '90%', mb: 1, mt: 1 }}>
            <Paper id="search-form" elevation={2} component="form" sx={{ mb: 1, padding: '0px 0px', display: 'flex', alignItems: 'center', width: searchWidth }}>
                {allSd.state === 'hasValue' &&
                    <Autocomplete
                        id="search-autocomplete"
                        sx={{ width: "100%" }}
                        options={allSd.contents}
                        value={value}
                        onChange={(ev, val) => setValue(val)}
                        autoHighlight
                        clearOnEscape
                        disableCloseOnSelect={false}
                        getOptionLabel={(option) => `${option.label}`}
                        filterOptions={handleFilterOptions}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                {...props}
                            >
                                <OptionViewer option={option} onExecute={handleExecute} />
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField sx={{ mx: 1 }}
                                {...params}
                                placeholder={plh}
                                variant='standard'
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password" // disable autocomplete and autofill
                                }}
                            />
                        )}
                    />
                }
                <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
                    <Search />
                </IconButton>
            </Paper>
            {isLocalhost &&
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: searchWidth, mb: 1 }}>
                    <Typography variant='body2'>Service Descriptor Groups: {allSd?.contents?.length}</Typography>
                </Box>
            }
            {value && <ResultViewerWithTable value={value} />}
        </Box>
    )
}

export const ResultViewerWithTable = ({ value }: { value: ServiceDescriptorGrouped }) => {
    const preferitoExists = usePreferitoForSdExists()
    const startApplication = useStartApplication()
    const gdDic = useRecoilValue(groupDescriptorDicState)
    const handleStartAppl = (sd: ServiceDescriptor) => (ev: React.MouseEvent<HTMLDivElement>) => {
        // console.log(`Start ${sd.id}`)
        startApplication(sd)
    }

    //const handlePrefOnClick = (sd: ServiceDescriptor) => (ev: React.MouseEvent<HTMLDivElement>) => console.log(`Start ${sd.id}`)
    const editPreferito = useEditPreferito()

    const handlePrefOnClick = (sd: ServiceDescriptor) => (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        // console.log(`Imposta preferito per ${sd.id} => ${sd.urlDescription}`)

        editPreferito(sd)
    }

    return (
        <TableContainer sx={{ width: searchWidth }}>
            <TableView
                items={value.items}
                itemKey={(item: ServiceDescriptor) => item.id}
                thDefs={
                    <>
                        <TableCell><Typography color='primary'>Applicazione</Typography></TableCell>
                        <TableCell><Typography color='primary'>Descrizione</Typography></TableCell>
                        <TableCell sx={{ maxWidth: '40px', textAlign: 'center' }}><Typography color='primary'>Livello</Typography></TableCell>
                        <TableCell sx={{ maxWidth: '40px', textAlign: 'center' }}><Typography color='primary'>Preferito</Typography></TableCell>
                        <TableCell sx={{ maxWidth: '80px', textAlign: 'center' }}><Typography color='primary'>Info</Typography></TableCell>
                    </>
                }
                trDefs={(row: ServiceDescriptor) => (
                    <>
                        <TableCell onClick={handleStartAppl(row)} sx={{ cursor: 'pointer', fontWeight: row.enabled ? 'bold' : 'normal' }}>
                            {row.urlDescription}
                            {!row.guid && <span className="ms-2"><Stickies size={12} /></span>}
                        </TableCell>
                        <TableCell onClick={handleStartAppl(row)} sx={{ cursor: 'pointer' }}>{row.note}</TableCell>
                        <TableCell onClick={handleStartAppl(row)} sx={{ cursor: 'pointer', textAlign: 'center' }}>{row.livello}</TableCell>
                        <TableCell sx={{ cursor: 'pointer', maxWidth: '40px' }}>
                            <Box sx={{ textAlign: 'center' }}>
                                {!row.enabled
                                    ? <Star size={20} />
                                    : <Box onClick={handlePrefOnClick(row)}>
                                        <StarFill size={20} color={preferitoExists(row.id) ? prefStarColor : notPrefStarColor} />
                                    </Box>
                                }
                            </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: '80px' }}>
                            <Box style={{ textAlign: 'center' }}>
                                <Typography><em><small><small>({row.id}) Area: {gdDic[row.groupDescriptorId].label}</small></small></em></Typography>
                            </Box>
                        </TableCell>
                    </>
                )}
            />
        </TableContainer>
    )
}

export const OptionViewer = ({ option, onExecute }: { option: ServiceDescriptorGrouped, onExecute?: (sd: ServiceDescriptor) => void }) => {
    const preferitoExists = usePreferitoForSdExists()
    const handlePref = () => console.log(`Preferiti: fai qualcosa per ${option.label}`)
    const handleEsegui = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (onExecute && option.items.length === 1) {
            onExecute(option.items[0])
        }
    }
    const showYellowStar = (sdg: ServiceDescriptorGrouped) => sdg.items.reduce((p, c) => p || !!(preferitoExists(c.id)), false)
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "& > img": { mr: 2, flexShrink: 0 }
            }}
        >
            {option.label}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100px"
                }}
            >
                <Box onClick={handlePref}>
                    <StarFill size={20} color={showYellowStar(option) ? prefStarColor : notPrefStarColor} />
                </Box>
                <Button disabled={option.items.length > 1} onClick={handleEsegui}>Esegui</Button>
            </Box>
        </Box>
    )
}